/** @jsx jsx */
import { Button, Grid, IconButton } from "@cd2/components/build/es6"
import { Fields, Form2 } from "@cd2/forms/build/es6"
import { FormState } from "@cd2/forms/build/es6/Form2/FormState"
import { P } from "@cd2/text/build/es6"
import { css, jsx } from "@emotion/core"
import { useEffect, useRef, useState } from "react"
import { icons } from "../../../constants/icons"
import { useAsyncEffect } from "../../../hooks/useAsyncEffect"
import { IRecord } from "../../../types/schema"
import { spliceArray } from "../../../utils/arrays"

type IUserForSelect = IRecord<"skeem_users", "name" | "skeem_email">

export function MultiEmailField({ field, branchId }: { field: string; branchId?: string }) {
  const formRef = useRef<FormState | null>(null)
  const skeem = window.skeem
  const [data, setData] = useState({ email: "" })
  const [inputValue, setInputValue] = useState("")
  const [fieldOptions, setFieldOptions] = useState<{ label: string; value: string }[]>([])
  const [menuIsOpen, setMenuIsOpen] = useState(false)

  const [forkedInputValue, setForkedInputValue] = useState("")

  useEffect(() => {
    let timeout: any = undefined
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => {
      setForkedInputValue(inputValue)
    }, 250)
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [inputValue])

  useAsyncEffect(async () => {
    await loadUsers(forkedInputValue)
  }, [forkedInputValue])

  const emailField = Form2.useField<string[]>(field, {
    subscribe: true,
    defaultValue: [],
  })

  // Add new item
  function handleAdd(email: string) {
    emailField.setValue([...emailField.getValue(), email])
  }

  // Remove item
  function handleRemove(index: number) {
    emailField.setValue(emailField.getValue().filter((_, i) => i !== index))
  }

  // Change item
  function handleChange(index: number, value: string) {
    emailField.setValue(spliceArray(emailField.getValue(), index, 1, value))
  }

  async function loadUsers(query: string) {
    const opts = await skeem.fetch2<IUserForSelect[]>({
      model: {
        name: "skeem_users",
        attributes: ["name", "skeem_email"],
        filter: {
          and: [
            branchId && { matches: { branch: branchId } },
            { matches: { userAccountType: "branch" } },
            {
              or: [
                { matches: { name: { like: query } } },
                { matches: { skeem_email: { like: query } } },
                { matches: { name: { like: query } } },
              ],
            },
            ...emailField.getValue().map(email => ({ not: { matches: { email } } })),
          ].filter(Boolean),
        },
        paginate: { size: 10 },
      },
    })
    setFieldOptions(opts.map(v => ({ label: v.skeem_email, value: v.skeem_email })))
  }

  function handleFormSubmit(value: string) {
    const emailDataField = formRef.current?.getField("email")
    if (!value) {
      emailDataField?.setError("is required")
      return
    }
    const matched = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.exec(
      value,
    )
    if (!matched) {
      emailDataField?.setError("must be a valid email")
      return
    }
    emailDataField?.setError(undefined)
    setData({ email: "" })
    handleAdd(value)
  }

  return (
    <Form2.Form xRef={formRef} onChange={setData} data={data}>
      <Form2.WithField<{ label: string; value: string }>
        field="email"
        render={field => (
          <Grid
            gap={12}
            css={css`
              width: 100%;
            `}
          >
            {emailField.getValue().map((email, i) => (
              <Grid key={i} columns={[{ fr: 1 }, "max-content"]} gap={8}>
                <Fields.Email
                  placeholder="Email"
                  value={email}
                  onChange={value => handleChange(i, value)}
                />
                <IconButton
                  buttonStyle="text"
                  icon={icons.cross}
                  buttonColor="white"
                  onClick={() => handleRemove(i)}
                  square
                />
              </Grid>
            ))}
            <Fields.CreatableSelect
              label="Add Email"
              postFieldText={
                <P size="small">Search for users or manually enter an email address</P>
              }
              options={fieldOptions}
              onCreateOption={value => handleFormSubmit(value)}
              getOptionLabel={opt => opt.label}
              getOptionValue={opt => opt.value}
              onInputChange={setInputValue}
              inputValue={inputValue}
              onChange={value => field.setValue(value)}
              value={field.getValue()}
              onMenuOpen={() => setMenuIsOpen(true)}
              onMenuClose={() => setMenuIsOpen(false)}
              onKeyDown={event => {
                if (event.key === "Enter" && !menuIsOpen) {
                  event.preventDefault()
                  event.stopPropagation()
                  handleFormSubmit(field.getValue()?.value)
                }
              }}
              error={field.getError()}
              allowCreateWhileLoading
              isClearable
            />

            <div>
              <Button
                onClick={() => handleFormSubmit(field.getValue()?.value)}
                buttonStyle="outline"
                buttonColor="#333"
              >
                + Add
              </Button>
            </div>
          </Grid>
        )}
      />
    </Form2.Form>
  )
}
